import { useState } from 'react';
import ImageUploadField from '../../components/ImageUploadField';
import { FiX } from 'react-icons/fi';
import { apiCreateBanner, apiUpdateBanner } from '../../api';

const editableTitle = (title) =>
  /^(hero|store|promo|offer) banner \d+$/i.test(String(title || '').trim()) ? '' : title || '';

export const BannerForm = ({ banner, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: editableTitle(banner?.title),
    subtitle: banner?.subtitle || '',
    highlight: banner?.highlight || '',
    badge: banner?.badge || '',
    bgColor: banner?.bgColor || '',
    link: banner?.link || '',
    buttonText: banner?.buttonText || '',
    position: banner?.position || 0,
    type: banner?.type || 'hero',
  });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const isImageOnly = form.type === 'store' || form.type === 'offer';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        // On edit, send empty values too so old title/subtitle/link fields can
        // actually be cleared instead of being silently left unchanged.
        if (banner || v !== '') formData.append(k, v);
      });
      if (image) formData.append('image', image);

      if (banner) {
        await apiUpdateBanner(banner.id, formData);
      } else {
        await apiCreateBanner(formData);
      }
      onSaved();
    } catch { /* API client displays the error toast. */ } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-black">
            {banner ? 'Edit Banner' : 'Add Banner'}
          </h2>
          <button onClick={onClose} className="text-black hover:text-black">
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm text-black mb-1">Banner Type *</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="hero">Hero (Top Carousel)</option>
              <option value="store">Store (Image Grid - Ekamithila Stores)</option>
              <option value="promo">Promo (Bottom 3-Box Slider)</option>
              <option value="offer">Offer (Home Offers Image Grid)</option>
            </select>
            <p className="text-xs text-black mt-1">
              Store and Offer banners show only the image. Offer banners fill the three tiles beside Great Book Offers.
            </p>
          </div>
          {!isImageOnly && (
            <>
              <input
                type="text"
                placeholder="Title (optional)"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              />
              <input
                type="text"
                placeholder="Subtitle"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              />
              <input
                type="text"
                placeholder="Highlight text (shown in overlay)"
                value={form.highlight}
                onChange={(e) => setForm({ ...form, highlight: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              />
              <input
                type="text"
                placeholder="Badge text (e.g. NEW, SALE)"
                value={form.badge}
                onChange={(e) => setForm({ ...form, badge: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
              />
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="BG Color (Tailwind class)"
              value={form.bgColor}
              onChange={(e) => setForm({ ...form, bgColor: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
            <input
              type="number"
              placeholder="Position"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
          </div>
          <input
            type="text"
            placeholder="Link URL"
            value={form.link}
            onChange={(e) => setForm({ ...form, link: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
          />
          <input
            type="text"
            placeholder="Button Text"
            value={form.buttonText}
            onChange={(e) => setForm({ ...form, buttonText: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
          />
          <ImageUploadField label="Banner Image" currentImage={banner?.image}
            required={isImageOnly} onImageChange={setImage} />
          <div className="flex justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : banner ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
